import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import DashboardActivityItem from '@/components/dashboard/DashboardActivityItem';
import IngestUsageCard from '@/components/usage/IngestUsageCard';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';

export default function DashboardPage() {
  const { t, i18n } = useTranslation('pages');
  const { user } = useAuth();

  const {
    data: dashboardData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => apiClient.getDashboard(),
  });

  const { data: quota } = useQuery({
    queryKey: ['quota', user?.id],
    queryFn: () => apiClient.getUserQuota(user!.id),
    enabled: Boolean(user),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="mb-3 sm:mb-8">
          <h1 className="page-title">{t('dashboard.title')}</h1>
          <p className="page-subtitle">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-secondary-600">
            {t('dashboard.loading')}
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="mb-3 sm:mb-8">
          <h1 className="page-title">{t('dashboard.title')}</h1>
          <p className="page-subtitle">{t('dashboard.subtitle')}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800 font-medium">
            {t('dashboard.errorTitle')}
          </div>
          <div className="text-red-600 text-sm mt-1">
            {t('dashboard.errorMessage')}
          </div>
        </div>
      </div>
    );
  }

  const { stats, recentActivity } = dashboardData || {
    stats: {},
    recentActivity: { recentSessions: [], upcomingEvents: [] },
  };
  const recentSessions = recentActivity?.recentSessions;

  return (
    <div className="page-container">
      <div className="mb-3 sm:mb-8">
        <h1 className="page-title">{t('dashboard.title')}</h1>
        <p className="page-subtitle">{t('dashboard.subtitle')}</p>
      </div>

      {/* Stats Strip */}
      <div className="bg-white border border-secondary-200 rounded-lg grid grid-cols-2 lg:grid-cols-4 mb-3 sm:mb-8">
        {[
          {
            label: t('dashboard.stats.eventsAttended'),
            value: stats?.eventsAttended || 0,
          },
          {
            label: t('dashboard.stats.totalSessions'),
            value: stats?.totalSessions || 0,
          },
          {
            label: t('dashboard.stats.totalLaps'),
            value: stats?.totalLaps || 0,
          },
          {
            label: t('dashboard.stats.totalVehicles'),
            value: stats?.totalVehicles || 0,
          },
        ].map(({ label, value }) => (
          <div key={label} className="px-5 py-4">
            <p className="text-xs font-medium text-secondary-400 uppercase tracking-[.06em] mb-1">
              {label}
            </p>
            <p className="font-mono text-[22px] font-semibold text-secondary-900 leading-none tracking-tight">
              {value}
            </p>
          </div>
        ))}
      </div>

      {/* Monthly usage */}
      {quota && (
        <div className="mb-3 sm:mb-8">
          <IngestUsageCard quota={quota} />
        </div>
      )}

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6 mb-3 sm:mb-8">
        <div className="bg-white p-4 sm:p-6 rounded-lg border border-secondary-200">
          <h3 className="text-xs sm:text-sm font-medium text-secondary-500 uppercase tracking-wide">
            {t('dashboard.stats.favoriteTrack')}
          </h3>
          {stats?.favoriteTrack ? (
            <div className="mt-2 min-w-0">
              <p className="text-lg sm:text-xl font-bold text-secondary-900 truncate">
                {stats.favoriteTrack.name}
              </p>
              <p className="text-sm text-secondary-600">
                {t('dashboard.stats.sessions', {
                  count: stats.favoriteTrack.sessionCount,
                })}
              </p>
            </div>
          ) : (
            <p className="text-lg sm:text-xl font-bold text-secondary-900 mt-2">
              {t('dashboard.stats.na')}
            </p>
          )}
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border border-secondary-200">
          <h3 className="text-xs sm:text-sm font-medium text-secondary-500 uppercase tracking-wide">
            {t('dashboard.stats.mostUsedVehicle')}
          </h3>
          {stats?.mostUsedVehicle ? (
            <div className="mt-2 min-w-0">
              <p className="text-lg sm:text-xl font-bold text-secondary-900 truncate">
                {stats.mostUsedVehicle.name}
              </p>
              <p className="text-sm text-secondary-600">
                {t('dashboard.stats.sessions', {
                  count: stats.mostUsedVehicle.sessionCount,
                })}
              </p>
            </div>
          ) : (
            <p className="text-lg sm:text-xl font-bold text-secondary-900 mt-2">
              {t('dashboard.stats.na')}
            </p>
          )}
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-8">
        <div className="bg-white p-4 sm:p-6 rounded-lg border border-secondary-200">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">
            {t('dashboard.recentSessions')}
          </h2>
          {recentSessions && recentSessions.length > 0 ? (
            <div className="space-y-3">
              {recentSessions.map((session) => (
                <DashboardActivityItem
                  key={session.id}
                  to={`/sessions/${session.id}`}
                  title={session.title}
                  trailing={new Date(session.conductedAt).toLocaleDateString(
                    i18n.language
                  )}
                >
                  <p className="flex min-w-0 items-center">
                    <span className="min-w-0 max-w-[120px] truncate sm:max-w-none">
                      {session.track?.name}
                    </span>
                    <span
                      aria-hidden="true"
                      className="mx-1 h-1 w-1 flex-none rounded-full bg-current"
                    />
                    <span className="min-w-0 max-w-[120px] truncate sm:max-w-none">
                      {session.vehicle
                        ? session.vehicle.name ||
                          `${session.vehicle.year} ${session.vehicle.maker} ${session.vehicle.model}`
                        : t('pages:dashboard.unknownVehicle')}
                    </span>
                  </p>
                  <p className="whitespace-nowrap">
                    {t('dashboard.laps', { count: session.lapCount })}
                  </p>
                </DashboardActivityItem>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8 text-secondary-500">
              <p className="text-sm sm:text-base">
                {t('dashboard.noRecentSessions')}
              </p>
              <p className="text-xs sm:text-sm mt-2">
                {t('dashboard.noRecentSessionsMessage')}
              </p>
            </div>
          )}
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-lg border border-secondary-200">
          <h2 className="text-lg font-semibold text-secondary-900 mb-4">
            {t('dashboard.upcomingEvents')}
          </h2>
          {recentActivity?.upcomingEvents &&
          recentActivity.upcomingEvents.length > 0 ? (
            <div className="space-y-3">
              {recentActivity.upcomingEvents.map((event) => (
                <DashboardActivityItem
                  key={event.id}
                  to={`/events/${event.id}`}
                  title={event.name}
                >
                  <p className="truncate">{event.track.name}</p>
                  <p className="break-words">
                    {new Date(event.startDate).toLocaleDateString(
                      i18n.language
                    )}{' '}
                    –{' '}
                    {new Date(event.endDate).toLocaleDateString(i18n.language)}
                  </p>
                </DashboardActivityItem>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 sm:py-8 text-secondary-500">
              <p className="text-sm sm:text-base">
                {t('dashboard.noUpcomingEvents')}
              </p>
              <p className="text-xs sm:text-sm mt-2">
                {t('dashboard.noUpcomingEventsMessage')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
